(ns ui.view.entry-criteria.view
  "Stage entry criteria modal.

   Per scope (opportunity / target / target-profile) we mount an independent
   `ui.view.entity-creator.editor/view` instance against its own creator-state
   ident (see `ui.view.entry-criteria.model`). Person-association edits live
   in a separate state tree under the modal's root ident — see
   `ui.view.entry-criteria.{event,sub,associations}`.

   The user fills in missing fields and associations inline; the footer's
   confirm button is disabled while any scope still has open validations, any
   unresolvable write-disabled blocker exists, or any mandatory person-
   association hasn't been satisfied (at least one person selected). Submit
   assembles a `MoveToStageRequest` body (`build-move-to-stage-body`) and
   hands it to the caller-supplied `:on-submit!`.

   A pipeline whose `:entry-criteria-mode` is `:missing-any` or `:always` also
   passes a `:review`. The modal then shows every field that the config sets
   for the stage, filled or optional, and a row for each optional association
   that is still missing. Only the criteria decide whether Confirm is
   disabled, so an optional item never blocks it.

   See docs/functional/stage_entry_criteria/."
  (:require
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.registry :as registry]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.editors.entity-creator.utils :as editor-utils]
   [ui.view.entity-creator.editor :as editor]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as creator-u]
   [ui.view.entry-criteria.associations :as associations]
   [ui.view.entry-criteria.event]
   [ui.view.entry-criteria.model :as m]
   [ui.view.entry-criteria.sub]
   [ui.view.entry-criteria.utils :as entry-u]
   [ui.util :as uu]))

(defn block
  "Section wrapper analogous to the opportunity-creator's grey bordered panel,
   using tw2 semantic tokens: `bg-bg-primary` is the light-grey panel
   background (CSS var `--bg-primary` resolves to gray-50), and
   `border-card-border-default` is the closest semantic border token in tw2."
  [title & children]
  (into
   [:div
    {:class (uu/cm "tw2-flex tw2-flex-col tw2-gap-2 tw2-p-3 "
                   "tw2-bg-bg-primary tw2-border tw2-border-solid "
                   "tw2-border-card-border-default tw2-rounded")}
    [:div {:class "tw2-text-lg tw2-mb-1"} title]]
   children))

(defn scope-title
  "Heading for a scope-block. Target/target-profile use the entity-type-name;
   target-profile is suffixed with \" profile\" (e.g. \"Investor profile\").
   When the pipeline runs through an investor target-profile, the target is
   the investor's legal entity — for that case we surface it as
   \"Legal entity (Company)\" / \"Legal entity (Person)\" so the role is
   obvious. Opportunity keeps a generic label.

   `:entity` is the scope's entity-map; if provided, its display string is
   appended so the user sees which specific target/target-profile/opportunity
   the modal is acting on. This replaces the read-only name field rows the
   modal used to show inline."
  [{:keys [scope entity-type target-profile-type entity]}]
  (let [entity-type-name (entity-type-names/get-name entity-type :en)
        label (case scope
                :opportunity (loc/tr :opportunity)
                :target (if (= target-profile-type :entity.type/investor)
                          (loc/tr :creator/legal-entity-label
                                  [entity-type-name])
                          (entity-type-names/get-name entity-type :en))
                :target-profile (loc/tr :creator/entity-profile
                                        [entity-type-name]))
        display (some-> entity entity/display-string not-empty)]
    (cond-> label
      display (str " - " display))))

(defn scope-entity
  "Returns the entity-map for `scope`. Opportunity comes from the modal's
   `props`; the others from the prebuilt `move-ctx`."
  [{:keys [props move-ctx]} scope]
  (case scope
    :opportunity (:opportunity props)
    :target (:target-entity move-ctx)
    :target-profile (:target-profile-entity move-ctx)))

(defn scope-args
  "Builds the args map shared between `creator-m/initialize` and `editor/view`
   for a given scope. Returns nil when the scope has no entity, no config-path,
   or no fields to show (criteria-trim left it empty)."
  [{:keys [props move-ctx scope criteria review] :as args}]
  (let [scope-et (entry-u/scope-entity-type move-ctx scope)
        config-path (entry-u/scope-config-path move-ctx scope)
        entity (scope-entity args scope)
        fields-config (entry-u/trimmed-fields-config-for-scope
                       {:move-ctx move-ctx
                        :scope scope
                        :criteria criteria
                        :review review})]
    (when (and scope-et config-path entity
               (seq (:fields fields-config)))
      {:ident (m/scope-ident props scope)
       :entity-type scope-et
       :pipeline (:pipeline-entity move-ctx)
       :stage (:target-stage props)
       :stages (:stages move-ctx)
       :config-path config-path
       :entity-edits-path creator-m/entity-edits-path
       :entity entity
       :fields-config fields-config})))

(def field-schema-overrides
  "Investor identity fields stay hidden — the modal never lets the user relink
   the target-profile's legal entity. The old `:read-only?` overrides on per-
   entity-type `*/name` attributes were dropped: with the criteria-driven
   field filter, name rows only appear when they're an actual failing
   criterion, and in that case the user must be able to fix them."
  {:investor/name {:hidden? true}
   :investor/legal-entity {:hidden? true}})

(defn scope-block
  "Renders one grey-block + editor/view for a single scope. Initializes the
   per-scope creator state at mount and clears it at unmount via
   `r/with-let` (mount-time only)."
  [{:keys [args title]}]
  (r/with-let [_ (rf/dispatch-sync [creator-m/initialize args])]
    (let [pipeline (:pipeline args)
          full-config (:pipeline/creator-config pipeline)
          field-schemas (registry/get-schema @session/tenant-schema
                                             (:entity-type args))]
      [block title
       [editor/view
        args
        {:raw-config full-config
         :field-schemas field-schemas
         :field-schema-overrides field-schema-overrides
         :entity (:entity args)
         :fields-config (:fields-config args)}]])
    (finally
      (rf/dispatch [creator-m/clear-state {:ident (:ident args)}]))))

(defn scope-validations
  "Per-scope `m/entity-validations` subscription. Returns nil when the scope
   isn't active."
  [args]
  (when args
    @(rf/subscribe [creator-m/entity-validations args])))

(defn scope-edits
  "Per-scope edits map (the entity-edits subtree)."
  [args]
  (when args
    @(rf/subscribe [creator-m/entity-edits args])))

(defn any-write-disabled?
  [criteria]
  (some :write-disabled? criteria))

(defn association-criterion-label
  "Used in the footer tooltip when listing unsatisfied associations. Same
   human-readable label `associations/association-row` shows; the entity-type
   already appears in the label, so no scope prefix is needed."
  [{:keys [association-key write-disabled?]}]
  (cond-> (editor-utils/association-key-label
           {:only-entity-type? nil}
           association-key)
    write-disabled? (str " - " (loc/tr :entry-criteria/write-disabled))))

(defn- blocked-criterion-label
  "Human-readable field name(s) for a criterion the acting user cannot fill in.
   Group criteria carry `:attributes` rather than a single `:attribute`."
  [tenant-schema {:keys [attribute attributes]}]
  (->> (if attribute [attribute] attributes)
       (keep (fn [a] (attribute/attribute-label tenant-schema a)))
       (string/join ", ")))

(defn blocked-criteria-block
  "Names the criteria the acting user has no write access to.

   Without this the modal was a dead end: those fields are dropped by the
   editor (it removes hidden fields), so the scope block rendered
   \"No fields configured\" - which reads as a misconfiguration - while Confirm
   stayed disabled with the explanation buried in a tooltip on the disabled
   button. The move genuinely cannot proceed, so the honest thing is to say
   which fields are blocking it and that an administrator is needed.

   Associations are excluded: they have their own section and their own
   write-disabled suffix."
  [criteria]
  (let [blocked (entry-u/blocked-field-criteria criteria)]
    (when (seq blocked)
      (let [tenant-schema @session/tenant-schema]
        [block
         (loc/tr :entry-criteria/write-disabled-heading)
         [:div
          {:class "tw2-flex tw2-flex-col tw2-gap-1"}
          [:ul
           {:class "tw2-m-0 tw2-pl-4"}
           (doall
            (for [criterion blocked
                  :let [label (blocked-criterion-label tenant-schema criterion)]
                  :when (not-empty label)]
              ^{:key (str (:scope criterion) "-" (:attribute criterion) label)}
              [:li label]))]
          [:div
           {:class "tw2-text-sm tw2-text-secondary"}
           (loc/tr :entry-criteria/some-write-disabled)]]]))))

(defn verify-banner
  "The one banner of the modal, in every scenario: a blocked move, a review of a
   record that is complete, and a review of one that is not. It asks the user to
   verify the information and correct what is not accurate. What blocks the move
   is not said here. The fields are marked, and `blocked-criteria-block` names
   the ones that the user cannot edit."
  []
  [alert/info
   {}
   [:div
    {:class "flex flex-col gap-1"}
    [:div
     {:class "font-semibold"}
     (loc/tr :entry-criteria/verify-heading)]
    [:div
     (loc/tr :entry-criteria/verify-body)]]])

(defn body
  [{:keys [criteria review scopes->args scopes->entities] :as args}]
  (let [target-profile-type (get-in scopes->args [:target-profile :entity-type])
        title (fn [scope scope-a]
                (scope-title {:scope scope
                              :entity-type (:entity-type scope-a)
                              :target-profile-type target-profile-type
                              :entity (:entity scope-a)}))
        any-association-row? (boolean (seq (entry-u/association-rows
                                            {:criteria criteria
                                             :review review})))]
    [:div
     {:class "tw2-flex tw2-flex-col tw2-gap-3"}
     [verify-banner]
     [blocked-criteria-block criteria]
     (doall
      (for [scope entry-u/scopes
            :let [scope-a (get scopes->args scope)]
            :when scope-a]
        ^{:key (str scope)}
        [scope-block {:args scope-a :title (title scope scope-a)}]))
     (when any-association-row?
       [block (loc/tr :person-associations)
        [associations/associations-section
         {:ident (:ident args)
          :criteria criteria
          :review review
          :scopes->entities scopes->entities}]])]))

(defn footer
  [{:keys [criteria scopes->args on-cancel! on-submit!] :as args}]
  (let [target-profile-type (get-in scopes->args [:target-profile :entity-type])
        scope->title (fn [scope]
                       (scope-title
                        {:scope scope
                         :entity-type (get-in scopes->args [scope :entity-type])
                         :target-profile-type target-profile-type
                         :entity (get-in scopes->args [scope :entity])}))
        scope-validation-map (->> scopes->args
                                  (keep (fn [[scope scope-a]]
                                          (when-let [v (scope-validations scope-a)]
                                            [scope v])))
                                  (into {}))
        validations-present? (boolean (seq scope-validation-map))
        unsatisfied-associations
        (associations/unsatisfied-association-criteria
         {:ident (:ident args) :criteria criteria})
        write-disabled? (any-write-disabled? criteria)
        disabled? (or validations-present?
                      write-disabled?
                      (seq unsatisfied-associations))
        tooltip-parts
        (cond-> []
          validations-present?
          (conj (loc/tr :entry-criteria/fix-validation-in
                        [(string/join ", "
                                      (map scope->title
                                           (keys scope-validation-map)))]))
          (seq unsatisfied-associations)
          (conj (loc/tr :entry-criteria/missing-person-associations
                        [(string/join ", "
                                      (map association-criterion-label
                                           unsatisfied-associations))]))
          write-disabled?
          (conj (loc/tr :entry-criteria/some-write-disabled)))
        tooltip-text (when (seq tooltip-parts)
                       (string/join "; " tooltip-parts))]
    [:div
     {:class "tw2-flex tw2-justify-between tw2-w-full"}
     [button/secondary-hollow
      {:on-click on-cancel!}
      (loc/tr :cancel)]
     [button/primary-solid
      {:on-click on-submit!
       :disabled? disabled?
       :tooltip tooltip-text}
      (loc/tr :confirm)]]))

(defn build-scopes->args
  "Returns `{scope args-or-nil}` for each scope; values are nil when the scope
   isn't present for this opportunity or when criteria-trim left nothing to
   show. Used both to render the body and to collect edits / validations at
   submit."
  [{:keys [props move-ctx criteria review]}]
  (->> entry-u/scopes
       (map (fn [scope] [scope (scope-args {:props props
                                            :move-ctx move-ctx
                                            :scope scope
                                            :criteria criteria
                                            :review review})]))
       (into {})))

(defn build-scopes->entities
  "Returns `{scope entity-or-nil}` so the associations section can look up
   the source entity for a criterion (`get-in` straight off scopes->args
   works but reads odd at call sites)."
  [scopes->args]
  (->> scopes->args
       (map (fn [[scope scope-a]] [scope (some-> scope-a :entity)]))
       (into {})))

(defn normalize-scope-edits
  "Stamps `:db/id` / `:entity/type` onto a scope's pending edits and runs
   them through the entity-creator's `normalize-entity-edits` pipeline so
   ref-single `{:value :label}` shapes become raw ids, currency-symbols are
   filled in, and blank values are dropped — same shape the creator's
   `start-create` handlers send to the backend."
  [{:keys [edits entity-id entity-type tenant-schema default-currency]}]
  (let [entity-schema (registry/get-schema tenant-schema entity-type)]
    (creator-u/normalize-entity-edits
     {:entity-schema entity-schema
      :default-currency default-currency}
     (assoc edits
            :db/id entity-id
            :entity/type entity-type))))

(defn build-move-to-stage-body
  "Assembles the `MoveToStageRequest` body from the modal's collected state:
   opportunity (+ field edits) is always present; `:target` / `:target-profile`
   are included only when the user actually edited a field in that scope
   (so the backend's per-scope write-access assertion only fires when needed);
   `:associations` is included only when the user picked a person on a
   mandatory association row. `:target-stage-id` always uses the stage we're
   trying to move into. `:target-pipeline-id` is included only when the
   modal was opened as part of a change-pipeline flow.

   Per-scope edits go through `normalize-scope-edits` so ref-single picks
   reach the backend as raw ids (not `{:value :label}` option maps) and
   currency-values get default symbols where missing."
  [{:keys [props scopes->args] :as args}]
  (let [opportunity (:opportunity props)
        target-stage (:target-stage props)
        target-pipeline (:target-pipeline props)
        opp-args (:opportunity scopes->args)
        target-args (:target scopes->args)
        target-profile-args (:target-profile scopes->args)
        opp-edits (or (scope-edits opp-args) {})
        target-edits (when target-args
                       (scope-edits target-args))
        target-profile-edits (when target-profile-args
                               (scope-edits target-profile-args))
        associations-edits (associations/collect-edits args)
        tenant-schema @session/tenant-schema
        default-currency @session/default-currency
        normalize (fn [edits entity-id entity-type]
                    (normalize-scope-edits
                     {:edits edits
                      :entity-id entity-id
                      :entity-type entity-type
                      :tenant-schema tenant-schema
                      :default-currency default-currency}))]
    (cond-> {:target-stage-id (or (:db/id target-stage) target-stage)
             :opportunity (normalize opp-edits
                                     (:db/id opportunity)
                                     (:entity/type opportunity))}
      target-pipeline
      (assoc :target-pipeline-id (or (:db/id target-pipeline) target-pipeline))

      (seq target-edits)
      (assoc :target (normalize target-edits
                                (:db/id (:entity target-args))
                                (:entity-type target-args)))

      (seq target-profile-edits)
      (assoc :target-profile (normalize target-profile-edits
                                        (:db/id (:entity target-profile-args))
                                        (:entity-type target-profile-args)))

      (seq associations-edits)
      (assoc :associations associations-edits))))

(defn submit!
  "Builds the `MoveToStageRequest` body and hands it to `:on-submit!`. The
   modal doesn't issue the backend call itself — the caller (typically
   `frontend.view.entity/attempt-move-to-stage!`'s `do-move!`) wraps
   `entity-actions/*->move-to-stage!` and decides on optimistic-update /
   sync-wait around it."
  [{:keys [on-submit!] :as args}]
  (let [body (build-move-to-stage-body args)]
    (when (fn? on-submit!)
      (on-submit! body))))

(defn modal-view
  "Entry point from the modal-system bridge ns.

   `props` (from `attempt-move-to-stage!`):
   - `:criteria`     — seq of criteria items from the validator
   - `:review`       — optional; set when the pipeline's `:entry-criteria-mode`
                       asks for a review. Lists the attributes and optional
                       associations to show besides the criteria
   - `:opportunity`  — opportunity entity-map
   - `:target-stage` — target stage entity (or id)
   - `:title`        — optional override
   - `:on-submit!`   — `(fn [body])` invoked with the assembled
                       `MoveToStageRequest` body when the user confirms; the
                       caller is responsible for performing the backend call
   - `:on-cancel!`   — thunk fired when the user cancels

   Form-2 component: the outer let body runs once at mount so the ident,
   move-ctx, and per-scope `scopes->args` are stable across re-renders. If
   we recomputed any of these on every render, `m/add-ident` would mint a
   fresh gensym ident each pass and scope-block's mount-time `creator-m/initialize`
   dispatch would write state at a path nobody subscribes to anymore — every
   subview would then read nil entity-type."
  [{:keys [_shown? _topmost? close-modal! props]}]
  (let [props (m/add-ident props)
        move-ctx (entry-u/move-context
                  {:opportunity (:opportunity props)
                   :target-pipeline (:target-pipeline props)})
        target-stage-entity (let [ts (:target-stage props)]
                              (some-> (or (:db/id ts) ts) session/get-entity))
        props (assoc props :target-stage target-stage-entity)
        scopes->args (build-scopes->args {:props props
                                          :move-ctx move-ctx
                                          :criteria (:criteria props)
                                          :review (:review props)})
        scopes->entities (build-scopes->entities scopes->args)
        ident (:ident props)
        clear-state! (fn []
                       (rf/dispatch [m/clear-state {:ident ident}]))
        cancel! (fn []
                  (when-let [f (:on-cancel! props)] (f))
                  (clear-state!)
                  (close-modal!))
        submit-and-close! (fn []
                            (submit! {:ident ident
                                      :props props
                                      :scopes->args scopes->args
                                      :criteria (:criteria props)
                                      :on-submit! (:on-submit! props)})
                            (clear-state!)
                            (close-modal!))]
    (fn [{:keys [shown? _topmost?]}]
      [modal/view
       ;; Use shown? only — anding with topmost? unmounts the modal body when
       ;; a nested modal (e.g. the tag-create modal) stacks on top, wiping
       ;; per-scope creator state via scope-block's `r/with-let` finally.
       ;; Matches the opportunity-creator curtain convention.
       {:visible? shown?
        :variant :tw2-styles
        :hide! cancel!}
       (or (:title props)
           (loc/tr :entry-criteria/moving-to-stage
                   [(entity/display-string (:target-stage props))]))
       [body {:ident ident
              :criteria (:criteria props)
              :review (:review props)
              :scopes->args scopes->args
              :scopes->entities scopes->entities}]
       [footer {:ident ident
                :criteria (:criteria props)
                :scopes->args scopes->args
                :on-cancel! cancel!
                :on-submit! submit-and-close!}]])))
